/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The click-opened modals as lazy components: SignupModal, MidCheckerModal and
 * EstimateKwhModal each leave the entry chunk and load on the first open.
 *
 * Why these three: every one mounts through ClientPortal, which renders nothing
 * at SSR and on the first client render, so no byte of them is in the baked HTML
 * and nobody needs their code before a click — the one part of the first-load JS
 * that is pure overhead (~24 KB gzip, docs/perf-baseline.md). SmartChargerInfoModal
 * is deliberately NOT here: it is a native <dialog> whose copy is baked, so its
 * code has to be there at hydration.
 *
 * The contract, in two halves:
 *  - a host wraps the modal in <LazyOnOpen open={…}> (LazyOnOpen.tsx), so the
 *    chunk is requested on the first `open` and never before;
 *  - the trigger spreads `{...preloadOnIntent(loadX)}`, so hovering, focusing or
 *    touching it starts the same import ahead of the click, and the modal
 *    usually opens with its chunk already in. The loaders are memoised: the
 *    preload, the lazy() and a second host share ONE import, one request.
 *
 * Hosts import SignupModal, MidCheckerModal and EstimateKwhModal from here under
 * the modals' own names, so their JSX does not change. Pinned by
 * src/components/__tests__/LazyOnOpen.test.tsx and the host tests, which open
 * the modal and `await findBy…` it.
 */

import { lazy } from 'react';

/**
 * One import per module, however many callers. A rejected import is forgotten,
 * so the next caller retries it — a preload that failed on a dropped connection
 * must not make the real open fail with the same cached rejection (lazy() still
 * remembers its own failure; ErrorBoundary reloads once for a stale chunk).
 */
function memoiseImport<T>(importer: () => Promise<T>): () => Promise<T> {
  let pending: Promise<T> | undefined;
  return () => {
    pending ??= importer().catch((error: unknown) => {
      pending = undefined;
      throw error;
    });
    return pending;
  };
}

export const loadSignupModal = memoiseImport(() => import('./signup/SignupModal'));
export const loadMidCheckerModal = memoiseImport(() => import('./MidCheckerModal'));
export const loadEstimateKwhModal = memoiseImport(() => import('./EstimateKwhModal'));

export const SignupModal = lazy(() => loadSignupModal().then((m) => ({ default: m.SignupModal })));
export const MidCheckerModal = lazy(() =>
  loadMidCheckerModal().then((m) => ({ default: m.MidCheckerModal }))
);
export const EstimateKwhModal = lazy(() =>
  loadEstimateKwhModal().then((m) => ({ default: m.EstimateKwhModal }))
);

/**
 * Event handlers for a modal's trigger: the pointer arriving, the keyboard
 * landing on it, or a touch starting all begin the modal's import. A rejection
 * is swallowed on purpose — the preload is a hint, and the real import (the
 * lazy() on open) retries and reports its own failure.
 */
export function preloadOnIntent(load: () => Promise<unknown>): {
  onPointerEnter: () => void;
  onFocus: () => void;
  onTouchStart: () => void;
} {
  const preload = () => {
    load().catch(() => {});
  };
  return { onPointerEnter: preload, onFocus: preload, onTouchStart: preload };
}
